import { Drawer, Table, type TableProps } from 'antd'
import { useState, type FC } from 'react'
import { FaQuestionCircle } from 'react-icons/fa'

import { Button, Tag } from '@/components/elements'
import {
  LOGI_FIELD_GROUP_REFLECTION,
  SHIPMENT_ORDER_CSV_FIELD_REFERENCE,
  SHIPMENT_ORDER_CSV_FIELD_REFERENCE_HELP
} from '@/constants/common/message.constants'

import {
  LOGI_FIELD_GROUP_LABEL,
  LOGI_FIELD_GROUPS,
  LOGI_FIELDS,
  type LogiField
} from '../../models'

const HELP = SHIPMENT_ORDER_CSV_FIELD_REFERENCE_HELP

type HelpRow = LogiField & { key: string; reflection: string; note: string }

const columns: TableProps<HelpRow>['columns'] = [
  {
    title: HELP.COL_HEADER_NAME,
    key: 'name',
    width: 160,
    render: (_, row) => (
      <span className="text-xs font-medium text-secondary">{row.label}</span>
    )
  },
  {
    title: HELP.COL_HEADER_REQUIRED,
    key: 'required',
    width: 70,
    align: 'center',
    render: (_, row) =>
      row.required ? (
        <Tag size="small" color="red" className="m-0">
          {HELP.REQUIRED_LABEL}
        </Tag>
      ) : (
        <span className="text-2xs text-gray-2">{HELP.OPTIONAL_LABEL}</span>
      )
  },
  {
    title: HELP.COL_HEADER_REFLECTION,
    key: 'reflection',
    width: 260,
    render: (_, row) => (
      <span className="text-xs text-secondary">{row.reflection}</span>
    )
  },
  {
    title: HELP.COL_HEADER_DESCRIPTION,
    key: 'note',
    render: (_, row) => <span className="text-xs">{row.note}</span>
  }
]

/**
 * マッピング項目リファレンス（配送依頼 汎用CSVインポート #4728）。
 * マッピング先として選べる全項目（LOGI_FIELDS）を、実際にマッピング画面のSelectと同じ
 * グループ順（必須項目/配送情報/運賃・費用/明細項目/カスタムキー/配車済み情報）で一覧表示し、
 * 「必須か」「どこに反映されるか」「形式・補足」を示す。
 *
 * 汎用経路（保存フォーマット/フォーマット外）のファイル選択画面（FileView）と
 * マッピング画面（MappingView）の両方から開ける。
 */
export const FieldReferenceHelp: FC = () => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <Button
        size="small"
        icon={<FaQuestionCircle />}
        onClick={() => setIsOpen(true)}
        data-testid="shipment-order-csv-import__field-reference-help"
      >
        {HELP.OPEN}
      </Button>
      <Drawer
        title={HELP.TITLE}
        width={960}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        data-testid="shipment-order-csv-import__field-reference-help-drawer"
      >
        <div className="flex flex-col gap-5">
          <span className="text-xs text-secondary">{HELP.DESCRIPTION}</span>

          <div className="flex flex-col gap-1.5">
            <div className="text-xs font-medium text-secondary">
              {HELP.RULES_TITLE}
            </div>
            <ul className="m-0 flex list-disc flex-col gap-1 pl-5">
              {HELP.RULES.map((rule) => (
                <li key={rule} className="text-xs text-secondary">
                  {rule}
                </li>
              ))}
            </ul>
          </div>

          {LOGI_FIELD_GROUPS.map((group) => {
            const rows: HelpRow[] = LOGI_FIELDS.filter(
              (f) => f.group === group
            ).map((f) => ({
              ...f,
              key: f.value,
              reflection: LOGI_FIELD_GROUP_REFLECTION[group],
              note: SHIPMENT_ORDER_CSV_FIELD_REFERENCE[f.value]
            }))
            return (
              <div key={group} className="flex flex-col gap-1.5">
                <div className="text-xs font-medium text-secondary">
                  {LOGI_FIELD_GROUP_LABEL[group]}
                </div>
                <Table<HelpRow>
                  size="small"
                  bordered
                  rowKey="key"
                  pagination={false}
                  columns={columns}
                  dataSource={rows}
                />
              </div>
            )
          })}
        </div>
      </Drawer>
    </>
  )
}
